// app/admin/signin.tsx

import { googleSignIn, googleSignOut } from "@/lib/authGoogle";
import {
  checkCurrentUserIsAdmin,
  signOutAdminOrWrongUser,
} from "@/lib/admin/adminAuth";
import { router } from "expo-router";
import React, { useState } from "react";
import {
  ActivityIndicator,
  Alert,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";

export default function AdminSignInScreen() {
  const [busy, setBusy] = useState(false);

  async function handleAdminSignIn() {
    if (busy) return;

    try {
      setBusy(true);

      /**
       * Important:
       * Clear any owner session first.
       * This prevents the admin dashboard from checking the owner UID.
       */
      await googleSignOut();

      const credential = await googleSignIn();

      if (!credential) {
        return;
      }

      const adminCheck = await checkCurrentUserIsAdmin();

      if (!adminCheck.isAdmin) {
        await signOutAdminOrWrongUser();

        Alert.alert(
          "Admin access denied",
          adminCheck.reason ??
            "This Google account is not registered as an admin."
        );

        return;
      }

      router.replace("/admin");
    } catch (error) {
      Alert.alert(
        "Admin sign-in failed",
        error instanceof Error
          ? error.message
          : "Something went wrong while signing in as admin."
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <View style={styles.screen}>
      <View style={styles.card}>
        <Text style={styles.title}>Soko Grid Admin</Text>

        <Text style={styles.subtitle}>
          Sign in using the Google account that has role: admin in Firestore.
        </Text>

        <Pressable
          style={[styles.button, busy && styles.buttonDisabled]}
          onPress={handleAdminSignIn}
          disabled={busy}
        >
          {busy ? (
            <ActivityIndicator color="#ffffff" />
          ) : (
            <Text style={styles.buttonText}>Sign in as Admin</Text>
          )}
        </Pressable>

        <Text style={styles.note}>
          Owner accounts are blocked from the admin dashboard.
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: "#f8fafc",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },

  card: {
    width: "100%",
    maxWidth: 420,
    backgroundColor: "#ffffff",
    borderRadius: 22,
    padding: 24,
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },

  title: {
    fontSize: 26,
    fontWeight: "900",
    color: "#111827",
    textAlign: "center",
    marginBottom: 8,
  },

  subtitle: {
    fontSize: 15,
    color: "#4b5563",
    textAlign: "center",
    lineHeight: 22,
    marginBottom: 24,
  },

  button: {
    backgroundColor: "#111827",
    borderRadius: 14,
    paddingVertical: 15,
    alignItems: "center",
    justifyContent: "center",
  },

  buttonDisabled: {
    opacity: 0.65,
  },

  buttonText: {
    color: "#ffffff",
    fontSize: 16,
    fontWeight: "900",
  },

  note: {
    marginTop: 16,
    fontSize: 13,
    color: "#6b7280",
    textAlign: "center",
    lineHeight: 19,
  },
});
